import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Loader2, Plane, AlertTriangle, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientCartPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0, has_flights: false, count: 0 });
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const load = async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [cartRes, walletRes] = await Promise.all([
        axios.get(`${API}/client/cart`, h),
        axios.get(`${API}/client/wallet/summary`, h),
      ]);
      setCart(cartRes.data);
      setWallet(walletRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateQty = async (itemId, qty) => {
    try {
      if (qty <= 0) {
        await axios.delete(`${API}/client/cart/${itemId}`, { headers: getAuthHeaders() });
      } else {
        await axios.put(`${API}/client/cart/${itemId}?quantity=${qty}`, {}, { headers: getAuthHeaders() });
      }
      load();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/client/cart/${itemId}`, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const clearCart = async () => {
    if (!window.confirm('Vaciar todo el carrito?')) return;
    try {
      await axios.delete(`${API}/client/cart`, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/client/cart/checkout`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      if (res.data.has_flights) {
        toast.info('Recuerda contactar soporte para los vuelos', { duration: 6000 });
      }
      navigate('/cliente/ordenes');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en checkout');
    } finally { setCheckingOut(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const balance = wallet?.balance || 0;
  const insufficientFunds = balance < cart.total;

  return (
    <div className="space-y-6" data-testid="client-cart-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mi Carrito</h1>
          <p className="text-slate-500">{cart.count} servicios</p>
        </div>
        {cart.items.length > 0 && (
          <Button variant="outline" size="sm" onClick={clearCart} className="text-red-500 border-red-200" data-testid="clear-cart-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Vaciar
          </Button>
        )}
      </div>

      {/* Wallet Balance */}
      <div className={`p-3 rounded-lg flex items-center justify-between ${insufficientFunds && cart.items.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`} data-testid="wallet-balance-display">
        <div className="flex items-center gap-2">
          <Wallet className={`w-5 h-5 ${insufficientFunds && cart.items.length > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
          <span className={`text-sm font-medium ${insufficientFunds && cart.items.length > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            Saldo disponible: ${balance.toFixed(2)} USD
          </span>
        </div>
        {insufficientFunds && cart.items.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => navigate('/cliente/wallet')} className="text-red-600 border-red-300 text-xs" data-testid="recharge-btn">
            Recargar Wallet
          </Button>
        )}
      </div>

      {cart.items.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-4">Tu carrito esta vacio</p>
          <Button onClick={() => navigate('/cliente/servicios')} className="bg-emerald-600 hover:bg-emerald-700">
            Ver servicios <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {cart.items.map((item) => (
              <Card key={item.id} className="p-4 border-0 shadow-sm" data-testid={`cart-item-${item.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{item.service_name}</h3>
                      {item.requires_support && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <Plane className="w-3 h-3" /> Requiere soporte
                        </span>
                      )}
                    </div>
                    {item.description && <p className="text-sm text-slate-500 mt-1">{item.description}</p>}
                    <p className="text-sm text-slate-600 mt-1">${item.unit_price?.toFixed(2)} x {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-slate-900">${item.subtotal?.toFixed(2)}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <button onClick={() => updateQty(item.id, item.quantity - 1)} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid={`qty-minus-${item.id}`}>
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                      <button onClick={() => updateQty(item.id, item.quantity + 1)} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid={`qty-plus-${item.id}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeItem(item.id)} className="ml-2 p-1 text-red-400 hover:text-red-600" data-testid={`remove-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Flight Warning */}
          {cart.has_flights && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="flight-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Vuelos requieren atencion de soporte</p>
                <p className="text-xs text-amber-600 mt-1">Despues de hacer checkout, contacta a un representante por WhatsApp o chat para finalizar la reserva de tus vuelos.</p>
              </div>
            </div>
          )}

          {/* Checkout */}
          <Card className="p-5 border-0 shadow-md bg-gradient-to-r from-emerald-50 to-green-50" data-testid="cart-checkout">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-semibold text-slate-900">Total</span>
              <span className="text-2xl font-bold text-emerald-700">${cart.total.toFixed(2)} USD</span>
            </div>
            <div className="flex items-center justify-between mb-4 text-sm">
              <span className="text-slate-500">Saldo despues de pago:</span>
              <span className={`font-medium ${insufficientFunds ? 'text-red-600' : 'text-emerald-600'}`}>
                ${(balance - cart.total).toFixed(2)} USD
              </span>
            </div>
            {insufficientFunds && (
              <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="insufficient-funds-warning">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Saldo insuficiente. Necesitas recargar ${(cart.total - balance).toFixed(2)} USD mas.</span>
              </div>
            )}
            <Button
              onClick={checkout}
              disabled={checkingOut || insufficientFunds}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold disabled:opacity-50"
              data-testid="checkout-btn"
            >
              {checkingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" /> Confirmar y Pagar con Wallet
                </span>
              )}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-2">El pago se descuenta de tu wallet. La orden queda pendiente de aprobacion.</p>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClientCartPage;
